import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
    MessageSquareText,
    Phone,
    Instagram,
    MapPin,
    Loader2,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/track";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    PHONE_DISPLAY,
    TEL_HREF,
    SMS_HREF,
    INSTAGRAM_URL,
    INSTAGRAM_HANDLE,
    SERVICE_AREA_LONG,
    SERVICES,
} from "@/lib/site";
import { API } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/Breadcrumb";
import { useSiteText } from "@/lib/siteConfig";
import { EditableText } from "@/lib/editMode";

const initialForm = {
    name: "",
    phone: "",
    vehicle: "",
    service: "",
    message: "",
};

export default function Contact() {
    useSEO({
        title: "Contact — Text Or Call For A Quote",
        description:
            "Text or call Limit Break Performance for a quote. Mobile mechanic serving Virginia Beach, Norfolk, Chesapeake, and Portsmouth.",
    });

    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [searchParams] = useSearchParams();
    const phone = useSiteText("business.phone_display", PHONE_DISPLAY);
    const area = useSiteText("business.service_area_long", SERVICE_AREA_LONG);
    const insta = useSiteText("business.instagram_url", INSTAGRAM_URL);

    useEffect(() => {
        const presetService = searchParams.get("service");
        if (presetService) {
            setForm((f) => ({ ...f, service: presetService }));
        }
    }, [searchParams]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please add your name and phone number.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSubmitted(true);
            setForm(initialForm);
            toast.success("Got it — I'll text you back shortly.");
            track("generate_lead", {
                source: "contact_form",
                service: form.service || "unspecified",
                vehicle: form.vehicle ? "provided" : "blank",
            });
        } catch (err) {
            console.error(err);
            toast.error("Couldn't send. Please text me directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <Breadcrumb section="Contact" />
            <section className="border-b border-white/10 bg-black">
                <div className="lbp-container pt-8 pb-16 sm:pb-20 lg:pb-24">
                    <EditableText
                        path="contact.page.eyebrow"
                        defaultValue="Let's talk cars"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <EditableText
                        path="contact.page.title"
                        defaultValue="Contact"
                        as="h1"
                        className="mt-2 font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl"
                        data-testid="contact-hero-title"
                    />
                    <EditableText
                        path="contact.page.subtitle"
                        defaultValue="Text is fastest — I usually reply same day. Shoot over your vehicle and what's going on and I'll get you a quote."
                        multiline
                        as="p"
                        className="mt-4 max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed"
                    />
                </div>
            </section>

            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-14 sm:py-20 grid lg:grid-cols-5 gap-10">
                    {/* Contact channels */}
                    <div className="lg:col-span-2 space-y-4">
                        <a
                            href={SMS_HREF}
                            data-testid="contact-text-tile"
                            className="block rounded-sm border border-red-600/40 bg-red-600/10 hover:bg-red-600/20 transition-colors p-6"
                        >
                            <div className="flex items-center gap-3">
                                <span className="inline-flex w-10 h-10 items-center justify-center rounded-sm bg-red-600 text-white">
                                    <MessageSquareText className="w-5 h-5" />
                                </span>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-red-400 font-semibold">
                                        Preferred
                                    </div>
                                    <div className="font-display font-bold uppercase tracking-tight text-white text-lg">
                                        Text Me
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 font-display text-2xl font-black text-white">
                                {phone}
                            </div>
                        </a>

                        <a
                            href={TEL_HREF}
                            data-testid="contact-call-tile"
                            className="block rounded-sm border border-white/10 bg-zinc-900 hover:border-white/40 transition-colors p-6"
                        >
                            <div className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-red-500" />
                                <div className="font-display font-bold uppercase tracking-tight text-white text-lg">
                                    Call
                                </div>
                            </div>
                            <div className="mt-3 text-zinc-300 font-display text-xl font-bold">
                                {phone}
                            </div>
                        </a>

                        <a
                            href={insta}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-instagram-tile"
                            className="block rounded-sm border border-white/10 bg-zinc-900 hover:border-white/40 transition-colors p-6"
                        >
                            <div className="flex items-center gap-3">
                                <Instagram className="w-5 h-5 text-red-500" />
                                <div className="font-display font-bold uppercase tracking-tight text-white text-lg">
                                    Instagram
                                </div>
                            </div>
                            <div className="mt-3 text-zinc-300">
                                @{INSTAGRAM_HANDLE}
                            </div>
                        </a>

                        <div className="rounded-sm border border-white/10 bg-zinc-900 p-6">
                            <div className="flex items-center gap-3">
                                <MapPin className="w-5 h-5 text-red-500" />
                                <div className="font-display font-bold uppercase tracking-tight text-white text-lg">
                                    Service Area
                                </div>
                            </div>
                            <p className="mt-2 text-zinc-400 text-sm">
                                {area}
                            </p>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-3">
                        {submitted ? (
                            <div
                                data-testid="contact-success-state"
                                className="rounded-sm border border-green-500/30 bg-green-500/5 p-8 text-center"
                            >
                                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto" />
                                <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl">
                                    Message Sent
                                </h2>
                                <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                                    Thanks — I'll get back to you as soon as
                                    I'm out from under a car. Need it faster?
                                    Text me directly.
                                </p>
                                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                                    <a
                                        href={SMS_HREF}
                                        data-testid="contact-success-text-button"
                                        className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                                    >
                                        <MessageSquareText className="w-4 h-4" />
                                        Text Me
                                    </a>
                                    <button
                                        type="button"
                                        data-testid="contact-send-another"
                                        onClick={() => setSubmitted(false)}
                                        className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white px-6 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                                    >
                                        Send Another
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                data-testid="contact-form"
                                className="rounded-sm border border-white/10 bg-zinc-900 p-6 sm:p-8 space-y-5"
                            >
                                <div>
                                    <Label
                                        htmlFor="name"
                                        className="text-xs uppercase tracking-widest text-zinc-300 font-semibold"
                                    >
                                        Your Name
                                    </Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        data-testid="contact-input-name"
                                        placeholder="e.g. Alex"
                                        className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                        required
                                    />
                                </div>

                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <Label
                                            htmlFor="phone"
                                            className="text-xs uppercase tracking-widest text-zinc-300 font-semibold"
                                        >
                                            Phone
                                        </Label>
                                        <Input
                                            id="phone"
                                            name="phone"
                                            value={form.phone}
                                            onChange={onChange}
                                            data-testid="contact-input-phone"
                                            placeholder="(757) 555-0123"
                                            className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                            required
                                            type="tel"
                                        />
                                    </div>
                                    <div>
                                        <Label
                                            htmlFor="vehicle"
                                            className="text-xs uppercase tracking-widest text-zinc-300 font-semibold"
                                        >
                                            Vehicle
                                        </Label>
                                        <Input
                                            id="vehicle"
                                            name="vehicle"
                                            value={form.vehicle}
                                            onChange={onChange}
                                            data-testid="contact-input-vehicle"
                                            placeholder="2018 Mustang GT"
                                            className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label
                                        htmlFor="service"
                                        className="text-xs uppercase tracking-widest text-zinc-300 font-semibold"
                                    >
                                        Service Needed
                                    </Label>
                                    <select
                                        id="service"
                                        name="service"
                                        value={form.service}
                                        onChange={onChange}
                                        data-testid="contact-input-service"
                                        className="mt-2 w-full h-11 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-white focus:outline-none focus:ring-2 focus:ring-red-600"
                                    >
                                        <option value="">Choose one…</option>
                                        {form.service &&
                                            !SERVICES.some(
                                                (s) => s.title === form.service
                                            ) &&
                                            form.service !== "Other" && (
                                                <option value={form.service}>
                                                    {form.service}
                                                </option>
                                            )}
                                        {SERVICES.map((s) => (
                                            <option
                                                key={s.slug}
                                                value={s.title}
                                            >
                                                {s.title}
                                            </option>
                                        ))}
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <Label
                                        htmlFor="message"
                                        className="text-xs uppercase tracking-widest text-zinc-300 font-semibold"
                                    >
                                        Tell Me What's Going On
                                    </Label>
                                    <Textarea
                                        id="message"
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        data-testid="contact-input-message"
                                        placeholder="Brake squeal, coilover install, CEL for P0300, etc."
                                        className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 min-h-[120px]"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit-button"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-4 rounded-sm font-display font-black uppercase tracking-wide text-sm sm:text-base"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            <MessageSquareText className="w-4 h-4" />
                                            Send Request
                                        </>
                                    )}
                                </button>

                                <p className="text-xs text-zinc-500 text-center">
                                    Want it faster? Just text{" "}
                                    <a
                                        href={SMS_HREF}
                                        className="text-red-500 hover:underline"
                                    >
                                        {phone}
                                    </a>
                                    .
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
